import styled from 'styled-components'
import { rem } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { getCountryCodeFromLocale, getNativeNameForLocale, getVisibleLanguageOptions } from 'i18n/util/locale'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'

const LanguageWarningLabel = styled.span`
  display: inline-flex;
  gap: ${rem(8)};
  align-items: center;
`

function LocaleRedirectModal({ accountLocale, urlLocale, onKeepPageLanguage, onSwitchToAccountLanguage }: {
  accountLocale: string
  urlLocale: string
  onKeepPageLanguage: () => Promise<void> | void
  onSwitchToAccountLanguage: () => Promise<void> | void
}) {
  const [loading, setLoading] = useState(false)

  const intl = useIntl()
  const modalContext = useModalElementContext<'url' | 'auth'>()
  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(
    () => getVisibleLanguageOptions(languagesRemoteConfig),
    [languagesRemoteConfig],
  )
  const onClose = useCallback(() => modalContext.resolve('url'), [modalContext])

  const title = intl.formatMessage({
    id: 'jVH8voIfhVh0RZcbF60E',
    defaultMessage: 'Choose your language',
    description: 'Title for a modal asking the customer to resolve a URL language and account language conflict',
  })
  const urlLanguage = getNativeNameForLocale(urlLocale, languageOptions) ?? urlLocale
  const authLanguage = getNativeNameForLocale(accountLocale, languageOptions) ?? accountLocale

  const urlCountry = getCountryCodeFromLocale(urlLocale, languageOptions)
  const accountCountry = getCountryCodeFromLocale(accountLocale, languageOptions)

  return (
    <Modal
      title={title}
      loading={loading}
      isPrimaryActionDisabled={loading}
      isSecondaryActionDisabled={loading}
      primaryActionText={
        <LanguageWarningLabel>
          {accountCountry ? <Flag countryCode={accountCountry} aria-hidden /> : null}
          {authLanguage}
        </LanguageWarningLabel>
      }
      secondaryActionText={
        <LanguageWarningLabel>
          {urlCountry ? <Flag countryCode={urlCountry} aria-hidden /> : null}
          {intl.formatMessage({
            id: 'mvniesvnqwYRAJMKgWqJ',
            defaultMessage: 'Keep page language',
            description: 'Secondary action to keep the language currently present in the URL',
          })}
        </LanguageWarningLabel>
      }
      onPrimaryActionClick={() => {
        setLoading(true)
        onSwitchToAccountLanguage()
      }}
      onSecondaryActionClick={async() => {
        setLoading(true)
        await onKeepPageLanguage()
        setLoading(false)
      }}
      onClose={onClose}
    >
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '4Z4zmNyyNhN2PC0qkMZ9',
            defaultMessage:
              'This page is in {urlLanguage}, but your account language is {authLanguage}.',
            description:
              'Body text explaining a URL language and account language conflict',
          },
          { urlLanguage, authLanguage },
        )}
      </BodyText>
    </Modal>
  )
}

export default LocaleRedirectModal
